import { produce } from "immer";
import { get, set } from "lodash-es";
/**
 * A function that creates a primitive value handler for a nested property in a store.
 *
 * @template Store - The type of the store object.
 * @template P - The type of the nested property path.
 *
 * @param {StoreApi<Store>} store - The store object.
 * @param {P extends string ? P : never} path - The nested property path.
 * @param {ILeitenPrimitiveEffects<DotNestedValue<Store, P>, Store>} [effects] - Optional effects for manipulating the value.
 *
 * @throws {Error} The defined path does not exist.
 *
 * @returns {ILeitenPrimitive<DotNestedValue<Store, P>>} An object with methods for manipulating the primitive value.
 */
export const leitenPrimitive = (store, path, effects) => {
    const initialValue = get(store.getState(), path, "_empty");
    if (initialValue === "_empty") {
        throw new Error("[leitenPrimitive] The defined path does not exist");
    }
    const getState = () => {
        const value = get(store.getState(), path, "_empty");
        return value !== "_empty" ? value : initialValue;
    };
    const setState = (next) => {
        const prev = getState();
        const draftState = produce(store.getState(), (draft) => {
            set(draft, path, next);
        });
        const nextState = effects?.patchEffect
            ? { ...draftState, ...effects.patchEffect(next) }
            : draftState;
        store.setState(nextState);
        effects?.sideEffect?.({ prev, next });
    };
    const clear = () => {
        const nextState = produce(store.getState(), (draft) => {
            set(draft, path, initialValue);
        });
        store.setState(nextState);
    };
    return { set: setState, get: getState, clear };
};
// const storeCreator = () => ({
//   test: { a: [{ a: 12 }, { a: 11 }], b: 12 },
//   a: {
//     b: () => {
//       // eslint-disable-next-line no-console
//       console.log("aaa");
//     },
//   },
// });
// const useAuthStore = create(storeCreator);
// const controllers = leitenPrimitive(useAuthStore, "test");
// controllers.set({ b: 12, a: [] });
// export const [useContextAuthStore, Provider, useControllers] = leitenContext(storeCreator, {
//   applyStore: (store) => ({ test: leitenPrimitive(store, "test") }),
// });
// useControllers().test.set({ b: 12, a: [] });
